import { Loader2, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import { useRemoveTagFromParentMutation } from '~entities/parent/api/remove-tag-from-parent';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '~shared/shadcn/ui/alert-dialog';
import { Button } from '~shared/shadcn/ui/button';

interface RemoveTagFromParentProps {
  parentId: string;
  tagName: string;
  tagId: string;
}

export function RemoveTagFromParent({
  parentId,
  tagName,
  tagId,
}: RemoveTagFromParentProps) {
  const { mutateAsync: removeTagFromParent, isPending } =
    useRemoveTagFromParentMutation();
  const [open, setOpen] = useState(false);

  const handleRemove = async () => {
    try {
      await removeTagFromParent({ parentId, tagIds: [tagId] });
      toast.success(`Tag "${tagName}" removed from parent successfully!`);
      setOpen(false);
    } catch (error) {
      console.error('Error removing tag from parent:', error);
      toast.error('Failed to remove tag from parent. Please try again.');
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          variant='ghost'
          size='icon'
          className='size-fit p-0 transition-all duration-200 hover:bg-transparent [&_svg]:size-3'
        >
          <X />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Remove Tag</AlertDialogTitle>
          <AlertDialogDescription>
            Are you sure you want to remove the tag &quot;{tagName}
            &quot; from this parent? This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isPending}>Cancel</AlertDialogCancel>
          <Button
            onClick={handleRemove}
            disabled={isPending}
            variant='destructive'
          >
            {isPending ? (
              <>
                <Loader2 className='mr-2 size-4 animate-spin' />
                Removing...
              </>
            ) : (
              'Remove'
            )}
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
